import React from 'react';
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { format, isAfter, parseISO } from 'date-fns';

const formatDate = (date) => {
  if (!date) return '-';
  return format(new Date(date), 'dd.MM.yyyy');
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const InvoiceDetail = ({ invoice, onClose, onEdit }) => {
  if (!invoice) return null;
  
  const isOverdue = !invoice.bezahltAm && invoice.faellig && isAfter(new Date(), parseISO(invoice.faellig));
  
  // Calculate payment status
  let statusColor = 'default';
  let statusText = 'Offen';
  let statusIcon = <ReceiptIcon />;
  
  if (invoice.bezahltAm) {
    statusColor = 'success';
    statusText = 'Bezahlt';
    statusIcon = <PaymentIcon />;
  } else if (isOverdue) {
    statusColor = 'error';
    statusText = 'Überfällig';
    statusIcon = <ReceiptIcon />;
  }

  return (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            Rechnung: {invoice.rechnungsNummer}
          </Typography>
          <Chip 
            icon={statusIcon}
            label={statusText}
            color={statusColor}
            variant={statusColor === 'default' ? 'outlined' : 'filled'}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Kundeninformationen
              </Typography>
              <Typography variant="body1">
                {invoice.kundenID?.bezeichnung || invoice.firmenname || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {invoice.kundenID?.strasse || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {invoice.kundenID?.plz} {invoice.kundenID?.ort || '-'}
              </Typography>
              
              {invoice.kundenID?.email && (
                <Typography variant="body2" color="text.secondary">
                  Email: {invoice.kundenID.email}
                </Typography>
              )}
              
              {invoice.kundenID?.telefon && (
                <Typography variant="body2" color="text.secondary">
                  Telefon: {invoice.kundenID.telefon}
                </Typography>
              )}
            </Paper>
          </Grid>
          
          {/* Invoice Details */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Rechnungsdetails
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Rechnungsnummer:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body1" fontWeight="medium">{invoice.rechnungsNummer}</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Rechnungsdatum:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body1">{formatDate(invoice.rechnungsDatum)}</Typography>
                </Grid>
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Fälligkeitsdatum:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body1">{formatDate(invoice.faellig)}</Typography>
                </Grid>
                
                {invoice.bezahltAm && (
                  <>
                    <Grid item xs={5}>
                      <Typography variant="body2" color="text.secondary">Bezahlt am:</Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body1" color="success.main">{formatDate(invoice.bezahltAm)}</Typography>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">Zahlungsbedingungen:</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body1">{invoice.zahlungsbedingungen || '-'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Financial Summary */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Beträge
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Nettobetrag:</Typography>
                    <Typography variant="body1">{formatCurrency(invoice.nettobetrag)}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Mehrwertsteuer:</Typography>
                    <Typography variant="body1">{formatCurrency(invoice.mehrwertsteuer)}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">MwSt-Satz:</Typography>
                    <Typography variant="body1">{invoice.mehrwertsteuersatz ? `${invoice.mehrwertsteuersatz}%` : '-'}</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">Gesamtbetrag:</Typography>
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {formatCurrency(invoice.gesamtbetrag)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          {/* Items and Notes */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Anmerkungen
              </Typography>
              <Typography variant="body2">
                {invoice.anmerkungen || 'Keine Anmerkungen vorhanden.'}
              </Typography>
            </Paper>
          </Grid>
          
          {/* Document Attachments */}
          {invoice.dokumente && invoice.dokumente.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Anhänge
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {invoice.dokumente.map((dokument, index) => (
                    <Chip
                      key={index}
                      icon={<AttachFileIcon />}
                      label={dokument.name || `Dokument ${index + 1}`}
                      variant="outlined"
                      onClick={() => window.open(dokument.url, '_blank')}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* Additional Information */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Weitere Informationen
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Zuordnung 1:</Typography>
                  <Typography variant="body1" gutterBottom>{invoice.zuordnung1 || '-'}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Zuordnung 2:</Typography>
                  <Typography variant="body1" gutterBottom>{invoice.zuordnung2 || '-'}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Erstellt am:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {invoice.createdAt ? formatDate(invoice.createdAt) : '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Aktualisiert am:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {invoice.updatedAt ? formatDate(invoice.updatedAt) : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Box>
          <Tooltip title="Drucken">
            <IconButton color="primary">
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Per E-Mail senden">
            <IconButton color="primary">
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Löschen">
            <IconButton color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Button onClick={onClose}>Schließen</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            Bearbeiten
          </Button>
        </Box>
      </DialogActions>
    </>
  );
};

export default InvoiceDetail;